import React from "react";
import { CheckCircle2, Clock, AlertTriangle } from "lucide-react";

/**
 * Badge di stato fattura riutilizzabile: pagata / in attesa / scaduta.
 * Palette coerente con il resto della dashboard (emerald/amber/rose)
 * mantenendo gli accenti Sunset Calm (forfy) per elementi neutri.
 *
 * Uso: <InvoiceStatusBadge status="paid" />
 * status: "paid" | "pending" | "overdue"
 * In alternativa puoi passare direttamente i dati fattura con `invoice` + `today`
 * e il badge deduce lo stato automaticamente.
 */

const STATUS_CONFIG = {
  paid: {
    label: "Pagata",
    Icon: CheckCircle2,
    light: "bg-emerald-50 text-emerald-600",
    dark: "bg-emerald-900/30 text-emerald-400",
  },
  pending: {
    label: "In attesa",
    Icon: Clock,
    light: "bg-amber-50 text-amber-600",
    dark: "bg-amber-900/30 text-amber-400",
  },
  overdue: {
    label: "Scaduta",
    Icon: AlertTriangle,
    light: "bg-rose-50 text-rose-600",
    dark: "bg-rose-900/30 text-rose-400",
  },
};

// Deduce lo stato di una fattura in base a paid/dueDate, coerente con la
// logica già usata in OverdueInvoicesCard/UpcomingDeadlines.
export function getInvoiceStatus(invoice, today = new Date().toISOString().split("T")[0]) {
  if (!invoice) return "pending";
  if (invoice.paid) return "paid";
  const dueDate = invoice.dueDate || invoice.issueDate;
  if (dueDate && dueDate < today) return "overdue";
  return "pending";
}

export default function InvoiceStatusBadge({
  status,
  invoice,
  today,
  darkMode = false,
  size = "md", // "sm" | "md"
  className = "",
}) {
  const resolvedStatus = status || getInvoiceStatus(invoice, today);
  const config = STATUS_CONFIG[resolvedStatus] || STATUS_CONFIG.pending;
  const { label, Icon, light, dark } = config;

  const sizeClasses = size === "sm" ? "px-2 py-0.5 text-[11px] gap-1" : "px-2.5 py-1 text-xs gap-1.5";
  const iconSize = size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5";

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${sizeClasses} ${
        darkMode ? dark : light
      } ${className}`}
    >
      <Icon className={iconSize} />
      {label}
    </span>
  );
}
